import { CURRENCY_BY_MARKET, type PromoCode } from '../dataProvider/types';
import { formatMoney } from '../cashbacks/formatMoney';

/**
 * Display + derived-state helpers for the Promo codes surface. The lifecycle
 * states are DISPLAYED, not enforced: there is no redemption engine, so the
 * surface surfaces the state honestly and blocks nothing (D-promo-codes). The
 * derivation reads the row plus an injected clock, so it is deterministic in tests.
 */

/** Human label per effect type. One member today; the enum grows additively. */
export const PROMO_EFFECT_LABEL: Record<PromoCode['effectType'], string> = {
  fixed_wallet_credit: 'Wallet credit',
};

/** "Wallet credit £10.00" — the effect with its currency-denominated value. */
export const formatEffect = (row: PromoCode): string =>
  `${PROMO_EFFECT_LABEL[row.effectType]} ${formatMoney(
    row.effectValue,
    CURRENCY_BY_MARKET[row.market],
  )}`;

const day = (iso: string): string => new Date(iso).toISOString().slice(0, 10);

/** "2026-05-01 to 2026-07-01" — the validity window as plain calendar dates. */
export const formatWindow = (row: PromoCode): string =>
  `${day(row.validFrom)} to ${day(row.validTo)}`;

/** "5 / 100" or "5 / uncapped" — used against the cap (cap is optional). */
export const formatRedemptions = (row: PromoCode): string =>
  `${row.usedCount} / ${row.maxRedemptions == null ? 'uncapped' : row.maxRedemptions}`;

/** The derived lifecycle state, by precedence: scheduled, expired, exhausted, live. */
export type PromoLifecycle = 'scheduled' | 'expired' | 'exhausted' | 'live';

export const promoLifecycle = (row: PromoCode, now: number): PromoLifecycle => {
  if (now < Date.parse(row.validFrom)) return 'scheduled';
  if (now > Date.parse(row.validTo)) return 'expired';
  if (row.maxRedemptions != null && row.usedCount >= row.maxRedemptions) {
    return 'exhausted';
  }
  return 'live';
};

export const LIFECYCLE_LABEL: Record<PromoLifecycle, string> = {
  scheduled: 'Scheduled',
  expired: 'Expired',
  exhausted: 'Exhausted',
  live: 'Live',
};

export const LIFECYCLE_COLOR: Record<
  PromoLifecycle,
  'default' | 'success' | 'warning' | 'error'
> = {
  scheduled: 'default',
  expired: 'error',
  exhausted: 'warning',
  live: 'success',
};
